import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  MenuItem,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TWButton, PageLayout, FileUploadZone, type FileUploadZoneRef } from '../../../shared/components';
import { getContractApiBase, getApiAuthHeaders } from '../utils/api';
import { getAccessToken } from '../../../shared/auth/okta';
import { colors, typography, textFieldStyles } from '../../../shared/styles/theme';
import type { ContractUploadResponse } from '../types/contracts';
import ContractReviewProgressStepper from './ContractReviewProgressStepper';

const ACCEPT = '.pdf,.doc,.docx';
const CONTRACT_TYPES = [
  { value: '', label: 'Select type (optional)' },
  { value: 'Contract', label: 'Contract' },
  { value: 'SOW', label: 'SOW' },
  { value: 'MSA', label: 'MSA' },
];

const ContractUpload: React.FC = () => {
  const navigate = useNavigate();
  const uploadZoneRef = useRef<FileUploadZoneRef>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clientName, setClientName] = useState('');
  const [contractType, setContractType] = useState('');
  const [isSigned, setIsSigned] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ContractUploadResponse | null>(null);

  const handleFileSelect = useCallback((file: File | null) => {
    setSelectedFile(file);
    setError(null);
    setResult(null);
  }, []);

  const buildUploadHeaders = (): Record<string, string> => {
    return getApiAuthHeaders(getAccessToken());
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }
    const trimmedClient = clientName.trim();
    if (!trimmedClient) {
      setError('Please enter client name');
      return;
    }
    if (!isSigned) {
      setError('Please select a contract status');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    const params = new URLSearchParams({ client_name: trimmedClient });
    if (contractType.trim()) params.set('contract_type', contractType.trim());
    params.set('is_signed', isSigned === 'signed' ? 'true' : 'false');

    try {
      const response = await axios.post<ContractUploadResponse>(
        `${getContractApiBase()}/upload?${params.toString()}`,
        formData,
        {
          headers: buildUploadHeaders(),
          onUploadProgress: (progressEvent) => {
            const progress = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            setUploadProgress(progress);
          },
        }
      );

      const data = response.data;
      navigate(`/contract-review/${data.session_uuid}`, {
        state: { uploadResult: data },
      });
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { detail?: string | Array<{ msg?: string }> }; status?: number };
        message?: string;
      };
      console.error('Error uploading contract:', err);
      const detail = ax.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg).filter(Boolean).join(', ')
            : ax.message;
      setError(msg ? `Upload failed: ${msg}` : 'Failed to upload contract. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setClientName('');
    setContractType('');
    setIsSigned('');
    setResult(null);
    setError(null);
    uploadZoneRef.current?.clearInput();
  };

  const handleReviewClauses = () => {
    if (result?.session_uuid) {
      navigate(`/contract-review/${result.session_uuid}`, {
        state: { uploadResult: result },
      });
    }
  };

  const headerRightActions = (
    <Button
      variant="text"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/contract-review')}
      sx={{
        color: colors.text.primary,
        textTransform: 'none',
        fontSize: typography.fontSize.base,
        fontWeight: 500,
        '&:hover': { backgroundColor: colors.background.hover },
      }}
    >
      Back to Contract Review
    </Button>
  );

  return (
    <PageLayout rightActions={headerRightActions}>
      <Box sx={{ mb: '24px', textAlign: 'left' }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: typography.fontSize['2xl'],
            color: colors.text.primary,
            mb: '12px',
            lineHeight: 1.2,
          }}
        >
          Upload contract
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: colors.text.secondary,
            fontSize: typography.fontSize.lg,
            mb: 0,
            lineHeight: 1.5,
          }}
        >
          Upload a PDF or DOCX contract. OneSec will extract security clauses and assess them against our standards.
        </Typography>
        <ContractReviewProgressStepper activeStep={1} />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { fontSize: '0.95rem' } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {!result && (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 2,
            backgroundColor: colors.background.card,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <FileUploadZone
            ref={uploadZoneRef}
            accept={ACCEPT}
            supportedFormatsText="Supported formats: PDF, DOCX"
            file={selectedFile}
            onFileSelect={handleFileSelect}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          >
            <TextField
              fullWidth
              label="Client name"
              required
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client or contract name"
              sx={{ mb: 2, ...textFieldStyles }}
            />
            <TextField
              fullWidth
              select
              label="Contract type"
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
              sx={{ mb: 3, ...textFieldStyles }}
            >
              {CONTRACT_TYPES.map((opt) => (
                <MenuItem key={opt.value || 'none'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              required
              label="Contract status"
              value={isSigned}
              onChange={(e) => setIsSigned(e.target.value)}
              sx={{ mb: 3, ...textFieldStyles }}
            >
              <MenuItem value="unsigned">Unsigned</MenuItem>
              <MenuItem value="signed">Signed</MenuItem>
            </TextField>
          </FileUploadZone>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
            <TWButton
              label={isUploading ? 'Uploading...' : 'Upload contract'}
              onClick={handleUpload}
              loading={isUploading}
              disabled={!selectedFile || !clientName.trim() || !isSigned || isUploading}
              startIcon={!isUploading ? <CloudUploadIcon /> : undefined}
              sx={{ fontSize: typography.fontSize.lg, minWidth: 220 }}
            />
            {selectedFile && (
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isUploading}
                sx={{
                  minHeight: '48px',
                  minWidth: 120,
                  border: `1.5px solid ${colors.primary}`,
                  color: colors.primary,
                  textTransform: 'none',
                  fontSize: typography.fontSize.md,
                  fontWeight: 500,
                  '&:hover': {
                    border: `1.5px solid ${colors.primary}`,
                    backgroundColor: colors.background.disabled,
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {result && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            backgroundColor: colors.background.card,
            border: `1px solid ${colors.border.medium}`,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 600, color: colors.text.primary, mb: 2 }}>
            Upload complete
          </Typography>
          <Typography variant="body1" sx={{ color: colors.text.secondary, mb: 1 }}>
            {result.message ?? 'Your contract has been uploaded and clauses extracted.'}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
            Session: {result.session_uuid} · Pages: {result.page_count ?? '—'} · Clauses extracted: {result.clauses_count}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TWButton
              label="Review clauses"
              onClick={handleReviewClauses}
              sx={{ fontSize: typography.fontSize.md, minWidth: 180, minHeight: '48px' }}
            />
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                minHeight: '48px',
                minWidth: 180,
                border: `1.5px solid ${colors.primary}`,
                color: colors.primary,
                textTransform: 'none',
                fontSize: typography.fontSize.md,
                fontWeight: 500,
                '&:hover': {
                  border: `1.5px solid ${colors.primary}`,
                  backgroundColor: colors.background.disabled,
                },
              }}
            >
              Upload another contract
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/contract-review')}
              sx={{
                minHeight: '48px',
                minWidth: 150,
                border: `1.5px solid ${colors.primary}`,
                color: colors.primary,
                textTransform: 'none',
                fontSize: typography.fontSize.md,
                fontWeight: 500,
                '&:hover': {
                  border: `1.5px solid ${colors.primary}`,
                  backgroundColor: colors.background.disabled,
                },
              }}
            >
              Back to Contract Review
            </Button>
          </Box>
        </Paper>
      )}
    </PageLayout>
  );
};

export default ContractUpload;
